import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { withTheme, type WithTheme } from '@emotion/react';
import _ from 'lodash';
import {
  type ExperienceMultiRowSurveyCard,
  type ExperienceMultiSurveyCardReasonSection,
  type ExperienceMultiSurveyCardSection,
} from '@bbkAdminRedux/rtkq/experience.slice';
import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs, type Argument } from '@bbkAdminUtils/css-utils';
import { type DictionaryByKey } from '@bbkAdminUtils/utility-types';
import { type NextStepHandler } from '@bbkFunnel/events';
import { type BackwardsObject } from '@bbkFunnel/pages/Funnel';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import DesignElement from '@bbkFunnelComponents/design-element/design-element';
import ErrorBox from '@bbkFunnelComponents/error-box/error-box';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { type CompetitorOtherTextHandler } from '@bbkFunnelComponents/modals/sections/competition-other-text';
import {
  CompetitionContent,
  type CompetitorOtherTextClearHandler,
} from '@bbkFunnelComponents/survey/competition/competition-content';
import { FeedbackContent } from '@bbkFunnelComponents/survey/feedback/feedback';
import {
  ReasonsContent,
  type ReasonOtherTextClearHandler,
} from '@bbkFunnelComponents/survey/reasons/reasons-content';
import { type ReasonOtherHandler } from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import { Sentiment } from '@bbkFunnelComponents/survey/sentiment/sentiment';
import { SubmitSection } from '@bbkFunnelComponents/survey/submit/submit-section';
import Icon from '@bbkFunnelUtils/exit-icon';
import { type FunnelLocalState } from '@bbkFunnelUtils/funnel-local-state';
import isReasonOther, {
  reasonRequiresTextInput,
} from '@bbkFunnelUtils/reason-other';
import {
  REFS_CONTEXT_FUNNEL_PREFIX,
  RefsContext,
} from '@bbkFunnelUtils/refs.context';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

export type SurveyMultiRowResponse = RestMultiRowSurveyCard['provides'];

export type SurveyMultiRowResponseHandler = (
  e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  value: string | boolean,
  tier?: string,
  currentTier?: string,
  args?: {
    type: 'change';
  }
) => void;

const getMultirowSurveyQuestionsMap = (props: Pick<Props, 'card'>) => {
  const { card } = props;
  return _.keyBy(card.requires.multi_row_survey, (x) => x.name);
};

const clearLS = (LS: FunnelLocalState, namespace: string) => {
  const searchRes = LS.search(namespace);
  // clear in all cases except when a history event occurs
  if (searchRes && searchRes.length > 0) {
    if (
      !window.performance ||
      (window.performance &&
        window.performance.navigation &&
        window.performance.navigation.type !== 2)
    ) {
      searchRes.forEach((r) => {
        if (r.key) {
          LS.del(r.key);
        }
      });
    }
  }
};
const ShuffleOptions = (
  reasons: ExperienceMultiSurveyCardReasonSection
): ExperienceMultiSurveyCardReasonSection['options'] => {
  let shuffleItems = reasons.options.filter((r) => !isReasonOther(r.value));
  const fixedItems = reasons.options.filter((r) => isReasonOther(r.value));
  if (reasons.randomize) {
    shuffleItems = _.shuffle(shuffleItems);
  } else {
    shuffleItems = _.orderBy(shuffleItems, (x) => x.label, 'asc');
  }
  return shuffleItems.concat(fixedItems);
};
// Called by Funnel to generate a shuffled item set
export const GenerateItems = (
  reasons: ExperienceMultiSurveyCardReasonSection,
  LS: FunnelLocalState,
  LSNameSpace: string
): ExperienceMultiSurveyCardReasonSection['options'] => {
  // console.log('GenerateItems', reasons, LS, LSNameSpace, LSNSSearch);
  // Clear items based on navigation event
  clearLS(LS, LSNameSpace);

  let items: ExperienceMultiSurveyCardReasonSection['options'] = [];
  let found = false;
  // Has items stored or not
  if (LS.has(LSNameSpace)) {
    const resS = LS.get(LSNameSpace) as
      ExperienceMultiSurveyCardReasonSection['options'] | null;
    if (Array.isArray(resS)) {
      items = resS;
      found = true;
    }
  }

  if (!found) {
    items = ShuffleOptions(reasons);
    LS.put(LSNameSpace, items);
  }

  return items;
};
const renderReasonItems = (props: Pick<Props, 'card' | 'LS'>) => {
  const { LS } = props;
  const questionsMap = getMultirowSurveyQuestionsMap(props);

  const reasons =
    questionsMap.reason_for_leaving! as ExperienceMultiSurveyCardReasonSection;
  return GenerateItems(reasons, LS, `${reasons.name}-MR_ReasonItems`);
};

export type DirectFromSurveyCancelHandler = (action: RestModalForwards) => void;

type PropsWOTheme = {
  card: ExperienceMultiRowSurveyCard;
  handler: SurveyMultiRowResponseHandler;
  nextStep: NextStepHandler;
  continue: () => void;
  response: RestMultiRowSurveyCard['provides'];
  clearCompOtherText: CompetitorOtherTextClearHandler;
  handleCompOtherText: CompetitorOtherTextHandler;
  handleReasonOtherText: ReasonOtherHandler;
  clearReasonOtherText: ReasonOtherTextClearHandler;
  directFromSurveyCancel: DirectFromSurveyCancelHandler;
  handleBackwards: (backwards: BackwardsObject) => void;
  loading?: boolean;

  LS: FunnelLocalState;
  isPreview?: boolean;
};

type Props = WithTheme<PropsWOTheme, EmotionTheme>;
type State = {
  error: Partial<Record<keyof SurveyMultiRowResponse, string>>;
  checked: boolean;
  card: ExperienceMultiRowSurveyCard;
  questions: DictionaryByKey<ExperienceMultiSurveyCardSection, 'name'>;
  reasonItems: ExperienceMultiSurveyCardReasonSection['options'];
  compOtherText: string;
};

const SurveyMultiRowWOutTheme: React.FC<Props> = (props) => {
  const {
    card,
    response,
    handler,
    theme,
    nextStep,
    continue: onContinue,
    directFromSurveyCancel,
    isPreview,
    loading,
    handleBackwards,
    handleCompOtherText,
    clearCompOtherText,
    handleReasonOtherText,
    clearReasonOtherText,
    LS,
  } = props;

  const context = useContext(FunnelContext);

  const [error, setError] = useState<State['error']>({});
  const [checked, setChecked] = useState(false);
  const [compOtherText, setCompOtherText] = useState(
    response.competition_other || ''
  );

  const questions = useMemo(
    () => getMultirowSurveyQuestionsMap({ card }) as State['questions'],
    [card]
  );
  const reasonItems = useMemo(
    () => renderReasonItems({ card, LS }),
    [card, LS]
  );

  useEffect(() => {
    console.log('[Multi-Row Survey] PROPS :: ', props);
  }, []);

  const getPaperStyles = () => ({
    borderRadius: theme.borderRadius,
    border: theme.border,
    boxShadow: theme.boxShadow,
    background: theme.cardBackground,
  });

  const errorSection = (classes?: Argument) => {
    const cardError = _.get(card, 'requires.error_message', {});
    return (
      error &&
      Object.keys(error).length > 0 && (
        <div className={cs('survey-row errorgrp', classes)}>
          <ErrorBox {...cardError} />
        </div>
      )
    );
  };

  const handleChecked = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => {
    setChecked(value);
    handler(e, value, 'confirmation');
  };

  const verifySurvey = (action: RestModalForwards) => {
    const errors: Partial<Record<keyof SurveyMultiRowResponse, string>> = {};
    const verificationMessageSurvey =
      _.get(card, 'requires.verificationMessage') ||
      theme.formRequiredFieldError;
    const verificationMessages: Partial<
      Record<keyof SurveyMultiRowResponse, string>
    > = {};

    const reqItems = card.requires.multi_row_survey
      .map((q) => {
        if (q.is_required && !q.disabled && !q.hide_checkbox) {
          const verificationMessageQuestion =
            _.get(q, 'verificationMessage') || verificationMessageSurvey;
          verificationMessages[q.name] = verificationMessageQuestion;

          if (q.name === 'sentiment') {
            if (response[q.name] <= 0) {
              errors[q.name] = verificationMessageQuestion;
              return false;
            }
          } else if (q.name !== 'confirmation') {
            if (_.isEmpty(response[q.name])) {
              if (q.name === 'reason_for_leaving' && reasonItems.length === 0)
                return true;
              errors[q.name] = verificationMessageQuestion;
              return false;
            }
          }

          return response[q.name];
        }

        return true;
      })
      .filter((r) => r === false);

    // check other input for reason
    const reasonForLeaving = card.requires.multi_row_survey.find(
      (q) => q.name === 'reason_for_leaving'
    ) as ExperienceMultiSurveyCardReasonSection;

    if (
      reasonForLeaving &&
      reasonForLeaving.is_required &&
      !reasonForLeaving.disabled
    ) {
      const rflOT = response.reason_other;
      // Only reasons that *require* text (the Other reason) can block submission when left blank.
      // Reasons that merely *show* an optional text box must not make the survey invalid, so use
      // reasonRequiresTextInput here rather than isReasonOther/reasonShowsTextInput.
      const otherReasons = response.reason_for_leaving.filter(
        (r) =>
          r.name === 'reason_for_leaving' && reasonRequiresTextInput(r.value)
      );
      if (otherReasons.length === 1 && (rflOT === undefined || rflOT === '')) {
        errors.reason_for_leaving = verificationMessages['reason_for_leaving'];
        reqItems.push(false);
      }
    }

    const competition = card.requires.multi_row_survey.find(
      (q) => q.name === 'competition'
    );
    if (competition && competition.is_required && !competition.disabled) {
      if (
        _.lowerCase(response.competition) === 'other' &&
        !response.competition_other
      ) {
        errors.competition = verificationMessages['competition'];
        reqItems.push(false);
      }
    }

    if (reqItems.length > 0) {
      setError(errors);
      return;
    }

    setError({});

    if (action.action === 'direct_from_survey_cancel') {
      directFromSurveyCancel(action);
    } else {
      // next step sets the upcoming modal to show
      nextStep(action.action);
      // continue shows the modal
      onContinue();
    }
  };

  const surveyRow = (
    row: ExperienceMultiSurveyCardSection,
    required: boolean,
    required_label = 'required',
    content: React.ReactNode,
    value: unknown,
    classes = ''
  ) => {
    const { isPreview: contextIsPreview } = context;
    const text = row.label;
    const reqCls = `survey-required ${value ? 'good' : ''}`;
    return (
      <div
        className={cs('survey-row', classes, {
          'is-disabled': row.disabled,
          'survey-row-required': required,
        })}
        title={
          row.disabled && contextIsPreview
            ? 'This is a disabled question. It will not appear for a end user.'
            : undefined
        }
      >
        <div className="survey-question">
          {required && error[row.name] && (
            <div className="error-arrow" css={{ color: theme.errorVal }}>
              <Icon name="errorArrow" />
            </div>
          )}
          <div
            className="survey-text"
            css={{
              color: required && error[row.name] ? theme.errorVal : 'inherit',
            }}
          >
            {text}
          </div>
          {required ? <div className={reqCls}>{required_label}</div> : null}
        </div>
        {}
        <div className="survey-answer"> {content}</div>
      </div>
    );
  };

  const sentiment = () =>
    questions.sentiment && (
      <Sentiment
        response={response}
        handler={handler}
        error={error.sentiment}
        lowText={questions.sentiment.low_text}
        highText={questions.sentiment.high_text}
      />
    );

  const competitionContent = () =>
    questions.competition && (
      <CompetitionContent
        value={response.competition}
        questions={questions.competition}
        errorText={error.competition}
        showOther={_.lowerCase(response.competition) === 'other'}
        compOtherText={compOtherText}
        handleCompOtherText={(val, opts) => {
          setCompOtherText(val);
          handleCompOtherText(val, opts);
        }}
        handler={handler}
        clearCompOtherText={clearCompOtherText}
        LS={LS}
        LSNameSpace={`${questions.competition.name}-MR_CompetitionItems`}
        isPreview={isPreview}
      />
    );

  const reasonsContent = (question: ExperienceMultiSurveyCardReasonSection) => (
    <ReasonsContent
      items={reasonItems}
      response={response}
      handler={handler}
      nextStep={nextStep}
      continue={onContinue}
      question={question}
      handleReasonOtherText={handleReasonOtherText}
      clearReasonOtherText={clearReasonOtherText}
      tierValue="tier0"
      error={error.reason_for_leaving}
    />
  );

  const getSurveyContent = () => {
    const { isPreview: contextIsPreview } = context;
    return _.chain(card.requires.multi_row_survey)
      .sortBy((x) => x.order)
      .filter((row) => {
        if (contextIsPreview) {
          return true;
        }
        return !row.disabled;
      })
      .map((row, index) => {
        // last item is confirmation text
        const lastClass =
          index === card.requires.multi_row_survey.length - 2
            ? 'last-survey-row'
            : '';

        let surveyRowEl;
        let rowName = '';
        switch (row.name) {
          case 'competition':
            surveyRowEl = surveyRow(
              row,
              row.is_required,
              row.required_label || theme.formRequiredLabel,
              competitionContent(),
              response.competition
            );
            rowName = 'Competitors';
            break;

          case 'feedback':
            surveyRowEl = surveyRow(
              row,
              row.is_required,
              row.required_label || theme.formRequiredLabel,
              <FeedbackContent
                value={response.feedback}
                handler={handler}
                question={row}
                error={error.feedback}
              />,
              response.feedback
            );
            rowName = 'Feedback';
            break;

          case 'reason_for_leaving':
            if (!contextIsPreview && reasonItems.length === 0) {
              surveyRowEl = null;
            } else {
              surveyRowEl = surveyRow(
                row,
                row.is_required,
                row.required_label || theme.formRequiredLabel,
                reasonsContent(row),
                response.reason,
                cs('radiogrp')
              );
            }
            rowName = 'Survey';
            break;

          case 'sentiment':
            surveyRowEl = surveyRow(
              row,
              row.is_required,
              row.required_label || theme.formRequiredLabel,
              sentiment(),
              response.sentiment
            );
            rowName = 'Sentiment';
            break;

          default:
            break;
        }
        if (surveyRowEl) {
          return (
            <div className={cs('survey-row-wrapper', lastClass)} key={index}>
              <DesignElement name={rowName}>{surveyRowEl}</DesignElement>
            </div>
          );
        }
      })
      .value();
  };

  const submitSection = () => {
    const { isDesigner } = context;

    const confirmationData = questions.confirmation;
    const hasStickyFooter =
      confirmationData?.sticky_desktop || confirmationData?.sticky_mobile;

    const submitSectionCard = confirmationData && (
      <SubmitSection
        questions={{ confirmation: confirmationData }}
        handleCheck={handleChecked}
        checked={checked}
        loading={loading}
        card={card}
        handleBackwards={handleBackwards}
        handleForwards={() => verifySurvey(card.requires.forwards)}
      />
    );
    const rowName = 'Confirmation Editor';

    const backgroundColor = new TinyColor(theme.cardBackground)
      .darken(3)
      .toHexString();

    if (
      !isDesigner &&
      !confirmationData?.is_required &&
      card.requires.backwards.disabled &&
      card.requires.forwards.disabled
    ) {
      return null;
    }

    const paperStyles = getPaperStyles();

    return (
      <div
        className={cs('survey-submit-wrapper', {
          'survey-submit-sticky': !isDesigner && hasStickyFooter,
        })}
        css={[
          paperStyles,
          {
            background: backgroundColor,
          },
        ]}
      >
        <DesignElement name={rowName}>
          <div>
            <div className="survey-error-wrapper">
              {errorSection('noPad noMarg')}
            </div>
            {submitSectionCard}
          </div>
        </DesignElement>
      </div>
    );
  };

  const paperStyles = getPaperStyles();

  return (
    <div css={{ position: 'relative' }}>
      <div
        className={cs('survey multi-row-survey', makeClassNameSafe(card.name))}
        css={[paperStyles]}
      >
        {errorSection()}
        {getSurveyContent()}
        {submitSection()}
      </div>
    </div>
  );
};

const SurveyMultiRowEl = withTheme(SurveyMultiRowWOutTheme);
export const SurveyMultiRow: React.FC<PropsWOTheme> = (props) => {
  const refsPrefix = REFS_CONTEXT_FUNNEL_PREFIX;
  const refsCtx = useContext(RefsContext);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-survey`, sectionRef),
    [sectionRef]
  );
  return (
    <div ref={sectionRef}>
      <SurveyMultiRowEl {...props} />
    </div>
  );
};
